import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, TextField, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useProjectInitialization } from '../../../api/hooks/useProjectInitialization';
import { useProject } from '../../../api/hooks/useProjects';
import { useUpsertReifegrad, computeMaturityFromForm } from '../../../api/hooks/useReifegrad';
import { useAutosave } from '../../../hooks/useAutosave';
import { PmCard } from '../../../components/pm';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildMachbarkeitReport } from '../reports/machbarkeitReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';

interface MachbarkeitForm {
  machbarkeit_problem: string;
  /** Gleiches initialization-Feld wie „Hauptziel“ unter Ziele (PHQ-99). */
  ziele_haupt: string;
  machbarkeit_scope: string;
  machbarkeit_out_of_scope: string;
  rahmen_vorgaben: string;
  rahmen_annahmen: string;
}

/**
 * Schritt 1 — Machbarkeit (Scope). PILOT fuer globales Live-Speichern:
 * kein Speichern-Button, sondern Autosave mit 600ms-Debounce + Statusanzeige.
 * Pflichtfelder (Problem, Scope) gaten den Save; der Re-Sync aus Server-Daten
 * ist waehrend des Tippens (isDirty) gesperrt, um Cursor-Springen zu vermeiden.
 * Felder liegen in der gemeinsamen project_initialization-Tabelle; die in der
 * Besprechung entfernten Spalten (tech/wirt) bleiben unangetastet.
 */
export function MachbarkeitTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { data, isLoading, save } = useProjectInitialization(projectId);
  const upsertReifegrad = useUpsertReifegrad(projectId);

  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  const [form, setForm] = useState<MachbarkeitForm>({
    machbarkeit_problem: '',
    ziele_haupt: '',
    machbarkeit_scope: '',
    machbarkeit_out_of_scope: '',
    rahmen_vorgaben: '',
    rahmen_annahmen: '',
  });

  const autosave = useAutosave<MachbarkeitForm>({
    canSave: (f) => f.machbarkeit_problem.trim().length > 0 && f.machbarkeit_scope.trim().length > 0,
    onSave: async (f) => {
      // machbarkeit_tech/_wirt werden bewusst NICHT gepatcht — bleiben in der DB.
      await save({
        machbarkeit_problem: f.machbarkeit_problem.trim(),
        ziele_haupt: f.ziele_haupt.trim(),
        machbarkeit_scope: f.machbarkeit_scope.trim(),
        machbarkeit_out_of_scope: f.machbarkeit_out_of_scope.trim(),
        rahmen_vorgaben: f.rahmen_vorgaben.trim(),
        rahmen_annahmen: f.rahmen_annahmen.trim(),
      });
      // Reifegrad-Tracking Schritt 1: 5 Felder, 2 Pflicht (problem, scope).
      const filled = [
        f.machbarkeit_problem.trim().length > 0,
        f.machbarkeit_scope.trim().length > 0,
        f.machbarkeit_out_of_scope.trim().length > 0,
        f.rahmen_vorgaben.trim().length > 0,
        f.rahmen_annahmen.trim().length > 0,
      ];
      const level = computeMaturityFromForm(filled, [true, true, false, false, false]);
      try { await upsertReifegrad.mutateAsync({ schritt: 1, data: { reifegrad: level } }); } catch { /* best-effort */ }
    },
  });

  // Re-Sync aus Server-Daten NUR wenn nicht gerade getippt wird (isDirty-Guard).
  useEffect(() => {
    if (autosave.isDirty) return;
    setForm({
      machbarkeit_problem: data.machbarkeit_problem,
      ziele_haupt: data.ziele_haupt ?? '',
      machbarkeit_scope: data.machbarkeit_scope,
      machbarkeit_out_of_scope: data.machbarkeit_out_of_scope,
      rahmen_vorgaben: data.rahmen_vorgaben,
      rahmen_annahmen: data.rahmen_annahmen,
    });
  }, [
    autosave.isDirty,
    data.machbarkeit_problem,
    data.ziele_haupt,
    data.machbarkeit_scope,
    data.machbarkeit_out_of_scope,
    data.rahmen_vorgaben,
    data.rahmen_annahmen,
  ]);

  const update = (patch: Partial<MachbarkeitForm>) => {
    const next = { ...form, ...patch };
    setForm(next);
    autosave.schedule(next);
  };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('machbarkeit.title', 'Machbarkeit (Scope)') };
  const buildReport = () =>
    buildMachbarkeitReport({
      problem: data.machbarkeit_problem,
      scope: data.machbarkeit_scope,
      outOfScope: data.machbarkeit_out_of_scope,
      vorgaben: data.rahmen_vorgaben,
      annahmen: data.rahmen_annahmen,
    });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box id="pg-machbarkeit">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {t('machbarkeit.title', 'Machbarkeit (Scope)')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('machbarkeit.subtitle', 'Problemstellung, Scope, Out-of-Scope sowie Vorgaben und Annahmen.')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PdfExportButton meta={pdfMeta} build={buildReport} />
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} />
        </Stack>
      </Stack>

      <PmCard>
        <Stack spacing={2.5}>
          <TextField
            required
            multiline
            rows={3}
            label={t('machbarkeit.problem', 'Vision')}
            helperText={t('machbarkeit.problemHelper', 'Warum existiert das Projekt?')}
            value={form.machbarkeit_problem}
            onChange={(e) => update({ machbarkeit_problem: e.target.value })}
            fullWidth
          />
          <TextField
            multiline
            rows={2}
            label={t('ziele.haupt', 'Hauptziel')}
            helperText={t('ziele.hauptHelper', 'Zentrale Projektzielsetzung — was soll erreicht werden?')}
            value={form.ziele_haupt}
            onChange={(e) => update({ ziele_haupt: e.target.value })}
            fullWidth
          />
          <TextField
            required
            multiline
            rows={3}
            label={t('machbarkeit.scope', 'Scope / Projektumfang')}
            helperText={t('machbarkeit.scopeHelper', 'Was soll geliefert werden?')}
            value={form.machbarkeit_scope}
            onChange={(e) => update({ machbarkeit_scope: e.target.value })}
            fullWidth
          />
          <TextField
            multiline
            rows={2}
            label={t('machbarkeit.outOfScope', 'Out of Scope')}
            helperText={t('machbarkeit.outOfScopeHelper', 'Was soll explizit vom Projekt ausgeschlossen werden?')}
            value={form.machbarkeit_out_of_scope}
            onChange={(e) => update({ machbarkeit_out_of_scope: e.target.value })}
            fullWidth
          />
          <TextField
            multiline
            rows={3}
            label={t('rahmen.vorgaben', 'Vorgaben / Restriktionen')}
            helperText={t('rahmen.vorgabenHelper', 'Regulatorik, Standards, gesetzte Rahmenbedingungen')}
            value={form.rahmen_vorgaben}
            onChange={(e) => update({ rahmen_vorgaben: e.target.value })}
            fullWidth
          />
          <TextField
            multiline
            rows={3}
            label={t('rahmen.annahmen', 'Annahmen')}
            helperText={t('rahmen.annahmenHelper', 'Das Projekt wird ein Erfolg, wenn…')}
            value={form.rahmen_annahmen}
            onChange={(e) => update({ rahmen_annahmen: e.target.value })}
            fullWidth
          />
        </Stack>
      </PmCard>
    </Box>
  );
}
